/* The bulk quote quiz's priced ending (sections/st-quote-quiz.liquid). A small order in a standard
   design has nothing to quote (Paulius, 2026-10-15: "50 x 3 google card and keychain should just
   give price straight away at end"), so when every ticked product is on its first rough quantity
   ("Up to 50") and no logo option is chosen, the last screen leads with a price:
   - one line per ticked product and ticked platform: a Google card with Trustpilot ticked is
     priced as the Trustpilot card; with no platform ticked, the product as shown in the quiz;
   - each in the block's priced pack (50), or the largest smaller pack it comes in, changeable to
     any smaller pack: the visitor said "up to 50", and more is the quote's job;
   - "Add to cart" adds the packs through Shopify's cart API and opens the cart.
   The quote form stays below for anyone who still wants one. Any other answer, or a product a
   ticked platform does not come in, leaves the screen as the quote form alone.

   The catalogue is the quiz funnel's (snippets/st-funnel-data.liquid) and so are its rules for a
   product's format and packs (assets/st-funnel-rules.js). Money goes through @theme/st-money, the
   one formatter. Event (assets/st-track.js lists the others):
     st_quote_priced_add  Add to cart pressed on the priced ending (value, currency, items) */
import { money } from '@theme/st-money';

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

function init(panel) {
  if (panel.dataset.stReady) return;
  const form = panel.closest('form');
  const step = panel.closest('[data-st-last]');
  const rules = globalThis.stFunnelRules;
  let cat;
  try {
    cat = JSON.parse(panel.querySelector('[data-st-price-data]').textContent);
  } catch (e) {
    return;
  }
  if (!form || !step || !rules || !cat) return;
  panel.dataset.stReady = 'true';

  const list = panel.querySelector('[data-st-price-list]');
  const tpl = panel.querySelector('[data-st-price-line]');
  const add = panel.querySelector('[data-st-price-add]');
  const err = panel.querySelector('[data-st-price-err]');
  const submit = form.querySelector('[data-st-submit]');
  const pack = parseInt(panel.dataset.pack, 10) || 0;
  const fmt = (cents) => money(cents, panel.dataset.money, panel.dataset.currency);
  const products = cat.products.map((p) => ({ ...p, format: rules.formatOf(p.h, panel.dataset.words) }));
  // The pack each product was left on, so a change elsewhere does not reset it.
  const kept = new Map();
  let lines = null;

  // The platforms ticked anywhere in the quiz: an answer that names a platform collection.
  const platforms = () => {
    const ticked = new Set();
    form.querySelectorAll('[data-st-question] .st-tiles input:checked').forEach((input) => {
      const v = norm(input.dataset.stValue || input.value);
      cat.platforms.forEach((p) => (norm(p.key) === v || norm(p.label) === v) && ticked.add(p.key));
    });
    return [...ticked];
  };

  // The product of the same format for another platform: the same handle with the platform swapped
  // when there is one (google-white-card to trustpilot-white-card), else the first of that format.
  const counterpart = (own, format, platform) => {
    if (own && own.platform === platform) return own;
    const swapped = own && own.h.replace(own.platform, platform);
    return products.find((p) => p.h === swapped && p.platform === platform) || products.find((p) => p.format === format && p.platform === platform);
  };

  const build = () => {
    if (!pack) return null;
    const picked = [...form.querySelectorAll('[data-st-product]:checked')];
    if (!picked.length || form.querySelector('input[data-st-logo]:checked')) return null;
    const ticked = platforms();
    const out = [];
    for (const box of picked) {
      const qty = form.querySelector(`[data-st-qty-for="${CSS.escape(box.dataset.stProduct)}"] input:checked`);
      if (!qty || !qty.hasAttribute('data-st-small')) return null;
      const own = products.find((p) => p.h === box.dataset.stHandle);
      const format = own ? own.format : rules.formatOf(box.dataset.stHandle || '', panel.dataset.words);
      if (!format) return null;
      for (const platform of ticked.length ? ticked : own ? [own.platform] : []) {
        const product = counterpart(own, format, platform);
        if (!product) return null;
        if (out.some((l) => l.product.h === product.h)) continue;
        const all = rules.packsOf(product);
        const packs = all.some((p) => p.n <= pack) ? all.filter((p) => p.n <= pack) : all.slice(0, 1);
        if (!packs.length) return null;
        const chosen = packs.find((p) => p.id === kept.get(product.h)) || packs[packs.length - 1];
        out.push({ product, packs, chosen });
      }
    }
    return out.length ? out : null;
  };

  const total = () => lines.reduce((sum, l) => sum + l.chosen.price, 0);
  const label = () => panel.dataset.add.replace('__P__', fmt(total()));

  const priceLine = (li, l) => {
    li.querySelector('[data-f="price"]').textContent = fmt(l.chosen.price);
    li.querySelector('[data-f="each"]').textContent = l.chosen.n > 1 ? panel.dataset.each.replace('__P__', fmt(Math.round(l.chosen.price / l.chosen.n))) : '';
  };

  const render = () => {
    const next = build();
    const on = !!next;
    panel.hidden = !on;
    // The quote form's button steps back to a secondary one while Add to cart leads.
    submit?.classList.toggle('st-btn--pri', !on);
    submit?.classList.toggle('st-btn--sec', on);
    lines = next;
    if (!on) return;
    err.hidden = true;
    list.replaceChildren(
      ...lines.map((l, i) => {
        const li = tpl.content.firstElementChild.cloneNode(true);
        const img = li.querySelector('[data-f="img"]');
        if (l.product.img) img.src = l.product.img;
        else img.remove();
        li.querySelector('[data-f="title"]').textContent = l.product.title;
        li.querySelector('[data-f="packs"]').replaceChildren(
          ...l.packs.map((p) => {
            const lab = document.createElement('label');
            const input = document.createElement('input');
            const span = document.createElement('span');
            input.type = 'radio';
            input.name = `st-qp-${form.id}-${i}`;
            input.value = String(p.id);
            input.checked = p.id === l.chosen.id;
            input.addEventListener('change', () => {
              l.chosen = p;
              kept.set(l.product.h, p.id);
              priceLine(li, l);
              add.textContent = label();
            });
            span.textContent = String(p.n);
            lab.append(input, span);
            return lab;
          })
        );
        priceLine(li, l);
        return li;
      })
    );
    add.textContent = label();
  };

  add.addEventListener('click', async () => {
    if (!lines || add.disabled) return;
    const text = add.textContent;
    add.disabled = true;
    add.textContent = panel.dataset.adding;
    err.hidden = true;
    try {
      const res = await fetch(cat.routes.add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map((l) => {
            const properties = rules.propertiesFor(cat, l.chosen.id);
            return { id: l.chosen.id, quantity: 1, ...(Object.keys(properties).length ? { properties } : {}) };
          }),
        }),
      });
      if (!res.ok) throw new Error(String(res.status));
      try {
        window.Shopify?.analytics?.publish?.('st_quote_priced_add', {
          page: location.pathname,
          value: total() / 100,
          currency: cat.currency,
          items: lines.map((l) => ({ product: l.product.h, variant: l.chosen.id, pack: l.chosen.n, price: l.chosen.price / 100 })),
        });
      } catch (e) {}
      location.href = cat.routes.cart;
    } catch (e) {
      add.disabled = false;
      add.textContent = text;
      err.hidden = false;
    }
  });

  // The quiz's own answers, not the pack radios above (they reprice in place).
  form.addEventListener('change', (event) => {
    if (!panel.contains(event.target)) render();
  });
  // Back from the cart: the page can come back from the browser's cache with the button still busy.
  window.addEventListener('pageshow', () => {
    add.disabled = false;
    if (lines) add.textContent = label();
  });
  render();
}

const run = () => document.querySelectorAll('[data-st-price]').forEach(init);
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
else run();
document.addEventListener('shopify:section:load', run);
